package ru.villawonka.app.presentation.design.components

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.Button
import androidx.compose.material.ButtonDefaults
import androidx.compose.material.Colors
import androidx.compose.material.Icon
import androidx.compose.material.IconButton
import androidx.compose.material.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.ui.tooling.preview.Preview
import ru.villawonka.app.presentation.design.extensions.AppSpacer
import ru.villawonka.app.presentation.design.theme.AppColors
import ru.villawonka.app.presentation.design.theme.AppTheme
import villawonka.composeapp.generated.resources.Res
import villawonka.composeapp.generated.resources.ic_heart_24

@Composable
fun AppIconButton(
    modifier: Modifier = Modifier,
    enabled: Boolean = true,
    selected: Boolean = false,
    iconRes: DrawableResource,
    type: AppIconButtonType = AppIconButtonType.DEFAULT,
    size: AppIconButtonSize = AppIconButtonSize.STANDART,
    onClick: () -> Unit,
) {
    val backgroundColor = if (enabled) {
        when (type) {
            AppIconButtonType.SEMI_TRANSPARENT -> if (selected) {
                MaterialTheme.colors.filledButtonThirdBackgroundSelected
            } else {
                MaterialTheme.colors.filledButtonThirdBackgroundEnabled
            }.copy(alpha = 0.45f)
            AppIconButtonType.DEFAULT -> if (selected) {
                MaterialTheme.colors.filledButtonThirdBackgroundSelected
            } else {
                MaterialTheme.colors.filledButtonThirdBackgroundEnabled
            }
            AppIconButtonType.FAVORITE -> if (selected) {
                MaterialTheme.colors.filledButtonFavoriteBackgroundSelected
            } else {
                MaterialTheme.colors.filledButtonFavoriteBackgroundEnabled
            }
        }
    } else {
        when (type) {
            AppIconButtonType.SEMI_TRANSPARENT -> if (selected) {
                MaterialTheme.colors.filledButtonThirdBackgroundSelected
            } else {
                MaterialTheme.colors.filledButtonThirdBackgroundDisabled
            }.copy(alpha = 0.45f)
            AppIconButtonType.DEFAULT -> if (selected) {
                MaterialTheme.colors.filledButtonThirdBackgroundSelected
            } else {
                MaterialTheme.colors.filledButtonThirdBackgroundDisabled
            }
            AppIconButtonType.FAVORITE -> if (selected) {
                MaterialTheme.colors.filledButtonFavoriteBackgroundSelected
            } else {
                MaterialTheme.colors.filledButtonFavoriteBackgroundDisabled
            }
        }
    }
    val contentColor = if (enabled) {
        when (type) {
            AppIconButtonType.SEMI_TRANSPARENT -> if (selected) {
                MaterialTheme.colors.filledButtonThirdContentSelected
            } else {
                MaterialTheme.colors.filledButtonThirdContentEnabled
            }.copy(alpha = 0.75f)
            AppIconButtonType.DEFAULT -> if (selected) {
                MaterialTheme.colors.filledButtonThirdContentSelected
            } else {
                MaterialTheme.colors.filledButtonThirdContentEnabled
            }
            AppIconButtonType.FAVORITE -> if (selected) {
                MaterialTheme.colors.filledButtonFavoriteContentSelected
            } else {
                MaterialTheme.colors.filledButtonFavoriteContentEnabled
            }
        }
    } else {
        when (type) {
            AppIconButtonType.SEMI_TRANSPARENT -> if (selected) {
                MaterialTheme.colors.filledButtonThirdContentSelected
            } else {
                MaterialTheme.colors.filledButtonThirdContentDisabled
            }.copy(alpha = 0.75f)
            AppIconButtonType.DEFAULT -> if (selected) {
                MaterialTheme.colors.filledButtonThirdContentSelected
            } else {
                MaterialTheme.colors.filledButtonThirdContentDisabled
            }
            AppIconButtonType.FAVORITE -> if (selected) {
                MaterialTheme.colors.filledButtonFavoriteContentSelected
            } else {
                MaterialTheme.colors.filledButtonFavoriteContentDisabled
            }
        }
    }

    val backgroundWidth = when (size) {
        AppIconButtonSize.STANDART -> 56.dp
        AppIconButtonSize.SMALL -> 32.dp
    }

    val backgroundHeight = when (size) {
        AppIconButtonSize.STANDART -> 48.dp
        AppIconButtonSize.SMALL -> 32.dp
    }

    when (size) {
        AppIconButtonSize.SMALL -> IconButton(
            onClick = onClick,
            enabled = enabled,
            modifier = modifier
                .background(
                    color = backgroundColor,
                    shape = RoundedCornerShape(12.dp)
                )
                .size(
                    width = 32.dp,
                    height = 32.dp
                )
        ) {
            Icon(
                painter = painterResource(iconRes),
                contentDescription = null,
                tint = contentColor,
                modifier = Modifier.size(24.dp)
            )
        }
        AppIconButtonSize.STANDART -> Button(
            onClick = onClick,
            enabled = enabled,
            colors = ButtonDefaults.buttonColors(
                backgroundColor = backgroundColor,
                contentColor = contentColor,
            ),
            elevation = null,
            shape = RoundedCornerShape(12.dp),
            modifier = modifier
                .size(
                    width = 56.dp,
                    height = 48.dp
                )
        ) {
            Icon(
                painter = painterResource(iconRes),
                contentDescription = null,
                tint = contentColor,
                modifier = Modifier.size(24.dp)
            )
        }
    }
}

private val Colors.filledButtonThirdBackgroundEnabled: Color
    get() = if (isLight) AppColors.grayLight100 else AppColors.grayDark600

private val Colors.filledButtonThirdBackgroundSelected: Color
    get() = if (isLight) AppColors.grayLight300 else AppColors.grayDark500

private val Colors.filledButtonThirdBackgroundDisabled: Color
    get() = if (isLight) AppColors.grayLight900 else AppColors.grayDark700

private val Colors.filledButtonThirdContentEnabled: Color
    get() = if (isLight) AppColors.grayLight950 else AppColors.grayDark50

private val Colors.filledButtonThirdContentSelected: Color
    get() = if (isLight) AppColors.grayLight950 else AppColors.grayDark50

private val Colors.filledButtonThirdContentDisabled: Color
    get() = if (isLight) AppColors.grayLight700 else AppColors.grayDark500

/**
 * Favorite Content
 */
private val Colors.filledButtonFavoriteContentEnabled: Color
    get() = if (isLight) AppColors.grayLight950 else AppColors.grayDark50 // поменять в фигме

private val Colors.filledButtonFavoriteContentHovered: Color
    get() = if (isLight) AppColors.pink500 else AppColors.pink50

private val Colors.filledButtonFavoriteContentFocused: Color
    get() = if (isLight) AppColors.pink500 else AppColors.pink50

private val Colors.filledButtonFavoriteContentSelected: Color
    get() = if (isLight) AppColors.pink500 else AppColors.pink50

private val Colors.filledButtonFavoriteContentDisabled: Color
    get() = if (isLight) AppColors.grayLight700 else AppColors.grayDark500

/**
 * Favorite Background
 */
private val Colors.filledButtonFavoriteBackgroundEnabled: Color
    get() = if (isLight) AppColors.grayLight100 else AppColors.grayDark600

private val Colors.filledButtonFavoriteBackgroundHovered: Color
    get() = if (isLight) AppColors.pink200 else AppColors.pink500

private val Colors.filledButtonFavoriteBackgroundFocused: Color
    get() = if (isLight) AppColors.pink300 else AppColors.pink600

private val Colors.filledButtonFavoriteBackgroundSelected: Color
    get() = if (isLight) AppColors.pink200 else AppColors.pink700

private val Colors.filledButtonFavoriteBackgroundDisabled: Color
    get() = if (isLight) AppColors.grayLight900 else AppColors.grayDark600



@Preview
@Composable
private fun Preview() {
    Row {
        Column(
            modifier = Modifier
                .background(Color.White)
                .padding(16.dp)
        ) {

            AppTheme(isDarkTheme = false) {

                // Enabled

                AppIconButton(
                    iconRes = Res.drawable.ic_heart_24,
                    enabled = true
                ) {}

                AppSpacer(16.dp)

                // Disabled

                AppIconButton(
                    iconRes = Res.drawable.ic_heart_24,
                    enabled = false
                ) {}

            }
        }
        Column(
            modifier = Modifier
                .background(Color.Black)
                .padding(16.dp)
        ) {

            AppTheme(isDarkTheme = true) {

                // Enabled

                AppIconButton(
                    iconRes = Res.drawable.ic_heart_24,
                    enabled = true
                ) {}

                AppSpacer(16.dp)

                // Disabled

                AppIconButton(
                    iconRes = Res.drawable.ic_heart_24,
                    enabled = false
                ) {}

            }
        }
    }
}